'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn';

export const badgeVariants = cva(
  'inline-flex h-5.75 items-center gap-1.5 rounded-[7px] pr-2.25 pl-2 text-[11.5px] font-[620] whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'bg-primary-tint text-primary-strong',
        primary: 'bg-primary-tint text-primary-strong',
        neutral: 'bg-neutral-tint text-neutral-tint-foreground',
        secondary: 'bg-neutral-tint text-neutral-tint-foreground',
        success: 'bg-success-tint text-success',
        warning: 'bg-warning-tint text-warning-strong',
        destructive: 'bg-destructive-tint text-destructive-strong',
        info: 'bg-info-tint text-info',
        outline: 'border-border-strong text-foreground-soft border bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  dot?: boolean;
  live?: boolean;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, dot = false, live = false, children, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot ? (
        <span
          aria-hidden="true"
          className={cn(
            'size-1.5 shrink-0 rounded-full bg-current shadow-[0_0_0_2.5px_color-mix(in_srgb,currentColor_15%,transparent)]',
            live && 'animate-pulse-dot motion-reduce:animate-none',
          )}
        />
      ) : null}
      {children}
    </span>
  ),
);
Badge.displayName = 'Badge';
